import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';
import { SolidButton } from 'components/Button';
import SearchProblem from 'components/SearchProblem';
import { Checkbox } from 'components/Checkbox';
import { CustomDatePicker as DatePicker } from 'components/DatePicker';
import { calendarIcon } from 'assets/svgs/brandIcons';
import DownArrowIcon from 'assets/svgs/DownArrowIcon';
import moment from 'moment';
import {
  usePackageHotelNames,
  usePackageReferenceData,
} from 'hooks/usePackageTours';
import { productPath, withoutProduct } from 'utils/productPaths';
import { hotelPath } from 'utils/packageHotel';
import {
  defaultsFor,
  errorMessage,
  findDestination,
  isHotelChoice,
  missingSearchField,
  parseSearch,
  referenceNotice,
  resultsPage,
  searchDestination,
  toQuery,
} from 'utils/packageSearch';

import { PackageSearchState, OpenPanel } from './types';
import DestinationDropdown from './DestinationDropdown';
import TransportDropdown from './TransportDropdown';
import PassengersDropdown from './PassengersDropdown';
import PriceDropdown from './PriceDropdown';
import BoardTypeDropdown from './BoardTypeDropdown';
import ServicesDropdown from './ServicesDropdown';
import DaysDropdown from './DaysDropdown';
import RoomTypeDropdown from './RoomTypeDropdown';
import StarsDropdown from './StarsDropdown';

export type PackageSearch = ReturnType<typeof parseSearch>;

const toDate = (iso: string | null) =>
  iso ? moment(iso, 'YYYY-MM-DD').toDate() : null;

// WHIT-482: the mask's state from a search: the URL's on P2/P3, the
// defaults on the home page. The destination's name comes later, with the
// reference data.
const stateFromSearch = (search: PackageSearch): PackageSearchState => ({
  destination: null,
  destinationInput: '',
  dateFrom: toDate(search.dateFrom),
  dateTo: toDate(search.dateTo),
  transportType: search.transportType,
  selectedAirports: search.airports,
  adults: search.adults,
  children: search.childAges.length,
  childAges: search.childAges,
  maxPrice: search.maxPrice,
  boardType: search.boardType || '',
  services: search.amenities,
  minDays: search.minDays,
  maxDays: search.maxDays,
  starRating: search.category,
  roomTypes: search.roomTypes,
  noTransfer: search.directOnly,
});

// The mask's state as a search. What the mask does not show (the hotel
// list's guest-rating and tour-operator filters, its sort) is kept from the
// search the page was opened with; the page goes back to 1.
const searchFromState = (
  state: PackageSearchState,
  base: PackageSearch,
): PackageSearch => ({
  ...base,
  dateFrom: state.dateFrom ? moment(state.dateFrom).format('YYYY-MM-DD') : null,
  dateTo: state.dateTo ? moment(state.dateTo).format('YYYY-MM-DD') : null,
  minDays: state.minDays,
  maxDays: state.maxDays,
  adults: state.adults,
  childAges: state.childAges.filter(age => age >= 0),
  transportType: state.transportType,
  airports: state.transportType === 'FLIGHT' ? state.selectedAirports : [],
  directOnly: state.transportType === 'FLIGHT' && state.noTransfer,
  destination: searchDestination(state.destination),
  maxPrice: state.maxPrice,
  boardType: state.boardType || null,
  category: state.starRating,
  roomTypes: state.roomTypes,
  amenities: state.services,
  page: 1,
});

const PackageSearchField: React.FC<{
  onDetailsToggle?: (open: boolean) => void;
  // The search the results page (P2/P3) was opened with; absent on the home
  // page.
  initialSearch?: PackageSearch;
  // WHIT-484: which tab the home page opened; a results page's own search
  // says it instead.
  product?: 'package' | 'hotel';
  // WHIT-500: on the results page the settings row sits under the fields,
  // as the flight search's does over flight results.
  settingsBelow?: boolean;
}> = ({
  onDetailsToggle,
  initialSearch,
  product = 'package',
  settingsBelow = false,
}) => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const base = initialSearch || defaultsFor(product);
  // A hotel-only stay ("Csak szállás"): no transport, a check-in window,
  // nights instead of days.
  const hotelOnly = base.product === 'hotel';
  const {
    data: reference,
    isLoading: referenceLoading,
    error: referenceError,
  } = usePackageReferenceData(i18n.language, base.product);
  // WHIT-501: said in the destination list while it has nothing to list.
  const destinationNotice = referenceNotice({
    data: reference,
    isLoading: referenceLoading,
    error: referenceError,
  });
  const [showDetails, setShowDetails] = useState(false);
  const [openToDate, setOpenToDate] = useState(false);
  const [openPanel, setOpenPanel] = useState<OpenPanel>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<PackageSearchState>(() =>
    stateFromSearch(base),
  );
  // WHIT-503: hotels whose name matches what is typed, under the places. A
  // failed lookup says so there and leaves the places listed.
  const hotelNames = usePackageHotelNames(
    // Only while choosing: a chosen place's name is not a search for hotels.
    openPanel === 'destination' && !state.destination
      ? state.destinationInput
      : '',
    i18n.language,
    base.product,
  );

  // The reference data arrives after the first render and names the
  // destination the URL carries (its id alone is in the URL). Adjusted
  // during render, once, rather than in an effect.
  const [named, setNamed] = useState(false);
  if (reference && !named) {
    setNamed(true);
    const destination = findDestination(
      reference.destinations,
      base.destination,
    );
    if (destination && !state.destination) {
      setState(prev => ({
        ...prev,
        destination,
        destinationInput: prev.destinationInput || destination.name,
      }));
    }
  }

  const set = <K extends keyof PackageSearchState>(
    key: K,
    value: PackageSearchState[K],
  ) => setState(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpenPanel(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChildrenChange = (n: number) => {
    const newAges = Array.from(
      { length: n },
      (_, i) => state.childAges[i] ?? -1,
    );
    setState(prev => ({ ...prev, children: n, childAges: newAges }));
  };

  const handleFromDateChange = (d: Date | null) => {
    set('dateFrom', d);
    if (d) setOpenToDate(true);
  };

  const handleToDateChange = (d: Date | null) => {
    set('dateTo', d);
    setOpenToDate(false);
  };

  // Search is never greyed out: with something missing it takes the
  // traveller to that field instead (WHIT-493).
  const [passengersOpenRequest, setPassengersOpenRequest] = useState(0);
  // WHIT-494: once Search was pressed, the sentence under the form says
  // what is still missing, and follows the fields as they are filled in.
  const [searchAttempted, setSearchAttempted] = useState(false);
  const searchProblem = searchAttempted ? missingSearchField(state) : null;
  const handleSearch = () => {
    setSearchAttempted(true);
    const missing = missingSearchField(state);
    if (missing === 'dateFrom') {
      setOpenPanel(null);
      const start =
        containerRef.current?.querySelector<HTMLInputElement>(
          '#startDate input',
        );
      start?.focus();
      start?.click();
      return;
    }
    if (missing === 'dateTo') {
      setOpenPanel(null);
      setOpenToDate(true);
      return;
    }
    if (missing === 'childAges') {
      setOpenPanel(null);
      setPassengersOpenRequest(n => n + 1);
      return;
    }
    const search = searchFromState(state, base);
    setOpenPanel(null);
    // A hotel chosen by name opens its page, which lists all its offers.
    const chosen = state.destination;
    router.push({
      pathname:
        chosen && isHotelChoice(chosen)
          ? hotelPath(chosen.name, chosen.id, router.locale, search.product)
          : productPath(resultsPage(search), router.locale, search.product),
      // The address says the product (WHIT-505).
      query: withoutProduct(toQuery(search)),
    });
  };

  const togglePanel = (panel: Exclude<OpenPanel, null>) => {
    setOpenPanel(prev => (prev === panel ? null : panel));
  };

  // WHIT-497: the flight tab's settings row (containers/SearchSettings)
  // for what the tour operators filter on: travellers, board, stars, price.
  // The fields say where and when.
  const settingsRow = (
    <div
      className={`relative flex flex-wrap justify-center lg:justify-start gap-y-2 ${
        settingsBelow
          ? 'mt-[1.125rem] gap-x-8 lg:gap-x-10'
          : 'gap-x-8 lg:mb-2 xl:mb-[1.625rem]'
      }`}
    >
      <PassengersDropdown
        variant="setting"
        adults={state.adults}
        // The number of child passengers, not React children.
        // eslint-disable-next-line react/no-children-prop
        children={state.children}
        childAges={state.childAges}
        onAdultsChange={n => set('adults', n)}
        onChildrenChange={handleChildrenChange}
        openRequest={passengersOpenRequest}
        onChildAgeChange={(idx, age) => {
          const newAges = [...state.childAges];
          newAges[idx] = age;
          set('childAges', newAges);
        }}
      />
      <BoardTypeDropdown
        variant="setting"
        value={state.boardType}
        options={reference?.boardTypes || []}
        onChange={v => set('boardType', v)}
      />
      <StarsDropdown
        variant="setting"
        value={state.starRating}
        onChange={v => set('starRating', v)}
      />
      <PriceDropdown
        variant="setting"
        value={state.maxPrice}
        onChange={v => set('maxPrice', v)}
      />
    </div>
  );

  return (
    <div ref={containerRef} className="relative z-40 pb-2">
      {openPanel !== null && (
        <div
          className="fixed inset-0 bg-black/40 z-30"
          onClick={() => setOpenPanel(null)}
        />
      )}
      {!settingsBelow && settingsRow}

      {/* Single grid container: flex-col on mobile, aligned CSS grid on desktop.
          The dates column is at least 21rem, so two full dates ("September
          30.") fit; the other columns shrink and truncate (WHIT-493). */}
      <div
        className={`relative flex flex-col gap-2 lg:grid ${settingsBelow ? '' : 'mt-6 lg:mt-4 xl:mt-0'} lg:grid-cols-[minmax(0,1fr)_minmax(21rem,1.6fr)_minmax(0,1fr)_auto] lg:gap-x-2 lg:gap-y-3 lg:items-end`}
      >
        {/* Col 1 */}
        <DestinationDropdown
          value={state.destinationInput}
          destinations={reference?.destinations || []}
          notice={destinationNotice}
          hotels={hotelNames.hotels}
          hotelsLoading={hotelNames.isLoading}
          hotelsNotice={
            hotelNames.error ? errorMessage(hotelNames.error.code) : null
          }
          selected={state.destination}
          isOpen={openPanel === 'destination'}
          onToggle={() => togglePanel('destination')}
          onClose={() => setOpenPanel(null)}
          onChange={v => set('destinationInput', v)}
          onSelect={d => set('destination', d)}
        />

        {/* Col 2 */}
        <div className="flex flex-col gap-1">
          {/* Named for screen readers; the placeholders say it on screen,
              as on the flight tab (WHIT-493). */}
          <span className="sr-only">
            {hotelOnly ? t('Check-in between') : t('Departure / Return')}
          </span>
          {/* The calendar opens from this field, not from the search row
              (WHIT-493): see .calendar-under-field in styles/DatePicker.css. */}
          <div className="relative calendar-under-field h-12 flex items-center">
            <DatePicker
              placeholderFrom={
                (hotelOnly ? t('Earliest') : t('Departure')) || ''
              }
              placeholderTo={(hotelOnly ? t('Latest') : t('Return')) || ''}
              icon={calendarIcon}
              openToDate={openToDate}
              setOpenToDate={setOpenToDate}
              fromDate={state.dateFrom}
              toDate={state.dateTo}
              onFromDateChange={handleFromDateChange}
              onToDateChange={handleToDateChange}
              onEnter={handleSearch}
              deleteReturnDate={() => set('dateTo', null)}
            />
          </div>
        </div>

        {/* Col 3 */}
        {hotelOnly ? (
          // Where the package mask has transport.
          <DaysDropdown
            nights
            minDays={state.minDays}
            maxDays={state.maxDays}
            onChange={(min, max) =>
              setState(prev => ({ ...prev, minDays: min, maxDays: max }))
            }
          />
        ) : (
          <TransportDropdown
            transportType={state.transportType}
            selectedAirports={state.selectedAirports}
            airports={reference?.departureAirports || []}
            offersOwnTransport={
              !!reference?.transportTypes?.some(type => type.code === 'OWN')
            }
            isOpen={openPanel === 'transport'}
            onToggle={() => togglePanel('transport')}
            onClose={() => setOpenPanel(null)}
            onChange={(type, airports) =>
              setState(prev => ({
                ...prev,
                transportType: type,
                selectedAirports: airports,
              }))
            }
          />
        )}

        {/* Col 4: search button desktop only */}
        <SolidButton
          onClick={handleSearch}
          // The flight tab's Search button: same padding, same width.
          className="hidden lg:flex h-12 items-center !px-12 self-end shrink-0"
        >
          {t('Search')}
        </SolidButton>

        {/* Advanced row — auto-placed into grid row 2 on desktop */}
        {showDetails && (
          <>
            {/* Row 2 cols 1-3: 2-col mobile grid, transparent on desktop */}
            <div className="mt-2 lg:mt-0 grid grid-cols-1 min-[436px]:grid-cols-2 gap-2 lg:contents">
              <ServicesDropdown
                selected={state.services}
                options={reference?.amenities || []}
                onChange={v => set('services', v)}
              />
              <RoomTypeDropdown
                selected={state.roomTypes}
                options={reference?.roomTypes || []}
                onChange={v => set('roomTypes', v)}
              />
              {/* A stay's nights are on the first row already. */}
              {!hotelOnly && (
                <DaysDropdown
                  minDays={state.minDays}
                  maxDays={state.maxDays}
                  onChange={(min, max) =>
                    setState(prev => ({ ...prev, minDays: min, maxDays: max }))
                  }
                />
              )}
            </div>
            {!hotelOnly && (
              <div className="flex items-center justify-start lg:pb-3">
                <Checkbox
                  id="pkg-no-transfer"
                  name="noTransfer"
                  checked={state.noTransfer}
                  onChange={() => set('noTransfer', !state.noTransfer)}
                  className="!mx-0"
                >
                  <span className="text-sm">{t('Non-stop only')}</span>
                </Checkbox>
              </div>
            )}
          </>
        )}
      </div>

      {settingsBelow && settingsRow}

      <SearchProblem problem={searchProblem} className="mt-3" />

      {/* Advanced search toggle */}
      <div className="mt-3 flex justify-start">
        <button
          type="button"
          onClick={() => {
            const next = !showDetails;
            setShowDetails(next);
            onDetailsToggle?.(next);
          }}
          className="min-h-11 text-sm text-tt-dark-purple font-medium hover:underline flex items-center gap-1"
        >
          {showDetails ? t('Hide advanced search') : t('Advanced search')}
          <DownArrowIcon
            className={`h-3 w-3 ${showDetails ? 'rotate-180' : ''}`}
            color="var(--tt-dark-purple)"
          />
        </button>
      </div>

      {/* Mobile search button */}
      <SolidButton
        onClick={handleSearch}
        className="lg:hidden mt-4 w-1/2 mx-auto flex justify-center h-12"
      >
        {t('Search')}
      </SolidButton>
    </div>
  );
};

export default PackageSearchField;
